import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@h2/ui';
import { Button } from '@h2/ui';
import { Badge } from '@h2/ui';
import { AlertTriangle, Languages, Clock, BarChart3 } from 'lucide-react';
import { WorkPreset } from '../../stores/useWorkPresetStore';

interface PresetDeleteModalProps {
  preset: WorkPreset;
  onConfirm: () => void;
  onCancel: () => void;
}

export const PresetDeleteModal: React.FC<PresetDeleteModalProps> = ({
  preset,
  onConfirm,
  onCancel
}) => {
  const usageCount = preset.metadata?.statistics?.totalUsages || 0;
  const languageCount = preset.configData?.languageSettingsComponent?.targetLanguages?.length || 0;
  const taskCount = preset.configData?.workflowComponent?.taskNodes?.length || 0;

  return (
    <Dialog open onOpenChange={onCancel}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10">
              <AlertTriangle className="h-5 w-5 text-destructive" />
            </div>
            <div>
              <DialogTitle>프리셋 삭제</DialogTitle>
              <DialogDescription>
                이 작업을 되돌릴 수 없습니다.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {/* 프리셋 정보 */}
          <div className="rounded-lg border p-4">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-2xl">📋</span>
              <div>
                <h4 className="font-semibold">{preset.name}</h4>
                <p className="text-sm text-muted-foreground">{preset.description}</p>
              </div>
            </div>
            
            {/* 태그 */}
            <div className="flex gap-1 flex-wrap mb-3">
              {preset.tags.map(tag => (
                <Badge key={tag} variant="secondary" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>

            {/* 통계 */}
            <div className="grid grid-cols-3 gap-2 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <Languages className="h-3 w-3" />
                <span>{languageCount}개 언어</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                <span>{taskCount}개 단계</span>
              </div>
              <div className="flex items-center gap-1">
                <BarChart3 className="h-3 w-3" />
                <span>{usageCount}회 사용</span>
              </div>
            </div>
          </div>

          {/* 삭제 영향 */}
          <div className="space-y-2">
            <p className="text-sm font-medium">삭제 시 영향:</p>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• 이 프리셋을 사용하는 기존 작업은 영향받지 않습니다</li>
              <li>• 새로운 작업에서는 더 이상 선택할 수 없습니다</li>
              <li>• 삭제된 프리셋은 복구할 수 없습니다</li>
              {usageCount > 0 && (
                <li className="text-amber-600">• 이 프리셋은 {usageCount}번 사용되었습니다</li>
              )}
            </ul>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            취소
          </Button>
          <Button variant="destructive" onClick={onConfirm}>
            삭제
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
